import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CalendarDays, MapPin, ArrowRight, Image as ImageIcon, Send, Clock, CheckCircle, CreditCard } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../context/AuthContext';

const API_URL = "";

const formatDateRange = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const opts = { month: 'long', day: 'numeric' };
  const yearOpts = { ...opts, year: 'numeric' };
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', yearOpts)}`;
  }
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', yearOpts)}`;
};

const StatusBadge = ({ status }) => {
  const styles = {
    upcoming: 'bg-blue-50 text-blue-700 border border-blue-200',
    active: 'bg-green-50 text-green-700 border border-green-200',
    past: 'bg-stone-100 text-stone-500 border border-stone-200',
  };
  const labels = { upcoming: 'Upcoming', active: 'Now Showing', past: 'Past' };
  return (
    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${styles[status] || styles.past}`}>
      {labels[status] || status}
    </span>
  );
};

// openCall: the full call object (includes allow_non_member_submissions)
// existingSubmission: user's existing submission for this call (or undefined)
const SubmitArtworkButton = ({ openCall, existingSubmission }) => {
  const { user } = useAuth();
  const callId = openCall.id;
  const allowGuest = openCall.allow_non_member_submissions !== false;

  // Existing submission states
  if (existingSubmission) {
    if (existingSubmission.payment_status === 'pending') {
      return (
        <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={`complete-payment-btn-${callId}`}>
          <Link to={`/members/submissions/${callId}?edit=${existingSubmission.id}`}>
            <CreditCard size={14} className="mr-1.5" /> Complete Payment
          </Link>
        </Button>
      );
    }
    return (
      <Button asChild size="sm" variant="outline" className="border-cafa-red text-cafa-red hover:bg-red-50" data-testid={`continue-submit-btn-${callId}`}>
        <Link to={`/members/submissions/${callId}?edit=${existingSubmission.id}`}>
          <CheckCircle size={14} className="mr-1.5" /> Continue Submission
        </Link>
      </Button>
    );
  }

  // No existing submission
  const memberRoute = user
    ? `/members/submissions/${callId}`
    : `/members/login?redirect=/members/submissions/${callId}`;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button asChild size="sm" className="bg-cafa-red hover:bg-red-700 text-white" data-testid={`submit-btn-${callId}`}>
        <Link to={memberRoute}>
          <Send size={14} className="mr-1.5" /> Submit as Member
        </Link>
      </Button>
      {allowGuest && (
        <Button asChild size="sm" variant="outline" className="border-stone-300 text-stone-600 hover:bg-stone-50" data-testid={`guest-submit-btn-${callId}`}>
          <Link to={`/submissions/guest/${callId}`}>
            Submit as Guest
          </Link>
        </Button>
      )}
    </div>
  );
};

const DeadlineBadge = ({ deadline }) => {
  const d = new Date(deadline);
  const now = new Date();
  const daysLeft = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  const label = daysLeft <= 7
    ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`
    : `Due ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="deadline-badge">
      <Clock size={11} /> {label}
    </span>
  );
};

const ExhibitionCard = ({ exhibition, existingSubmission }) => {
  const hasImage = !!exhibition.hero_image_url;
  const openCall = exhibition.open_call;
  const hasClosed = exhibition.has_closed_call;

  return (
    <Card className="overflow-hidden border border-stone-200 hover:shadow-md transition-shadow" data-testid={`exhibition-card-${exhibition.slug}`}>
      <div className="sm:flex">
        {/* Image or placeholder */}
        <div className={`sm:w-64 sm:flex-shrink-0 ${hasImage ? '' : 'bg-stone-100'}`}>
          {hasImage ? (
            <img
              src={exhibition.hero_image_url}
              alt={exhibition.title}
              className="w-full h-48 sm:h-full object-cover"
            />
          ) : (
            <div className="w-full h-48 sm:h-full flex items-center justify-center bg-stone-100">
              <ImageIcon size={40} className="text-stone-300" />
            </div>
          )}
        </div>

        <CardContent className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <StatusBadge status={exhibition.status} />
              {openCall && <DeadlineBadge deadline={openCall.deadline} />}
            </div>
            <h3 className="text-xl font-serif text-stone-900 mb-2">{exhibition.title}</h3>
            {exhibition.summary && (
              <p className="text-stone-600 text-sm leading-relaxed mb-4 line-clamp-2">{exhibition.summary}</p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-stone-500">
              <span className="flex items-center gap-1.5">
                <CalendarDays size={15} />
                {formatDateRange(exhibition.start_date, exhibition.end_date)}
              </span>
              <span className="hidden sm:inline text-stone-300">|</span>
              <span className="flex items-center gap-1.5">
                <MapPin size={15} />
                {exhibition.location}
              </span>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <Button asChild variant="outline" size="sm" className="text-stone-700 border-stone-300 hover:bg-stone-50">
              <Link to={`/exhibitions/${exhibition.slug}`}>
                View Exhibition <ArrowRight size={14} className="ml-1" />
              </Link>
            </Button>
            {openCall && <SubmitArtworkButton openCall={openCall} existingSubmission={existingSubmission} />}
            {hasClosed && !openCall && (
              <span className="inline-flex items-center text-xs text-stone-400 bg-stone-50 border border-stone-200 px-3 py-1.5 rounded-full" data-testid="submissions-closed-badge">
                Submissions Closed (Deadline passed)
              </span>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

const SectionHeading = ({ children, count }) => (
  <div className="flex items-center gap-3 mb-6">
    <h2 className="text-lg sm:text-xl font-serif text-stone-800">{children}</h2>
    {count > 0 && (
      <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{count}</span>
    )}
  </div>
);

export const Exhibitions = () => {
  const { token } = useAuth();
  const [exhibitions, setExhibitions] = useState([]);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExhibitions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/exhibitions/public`);
        if (res.ok) setExhibitions(await res.json());
      } catch (e) {
        console.error('Failed to fetch exhibitions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchExhibitions();
  }, []);

  // Fetch user's submissions if logged in to show "Continue Submission"
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/submissions/my`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setUserSubmissions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  const active = exhibitions.filter(e => e.status === 'active');
  const upcoming = exhibitions.filter(e => e.status === 'upcoming');
  const past = exhibitions.filter(e => e.status === 'past');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cafa-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="exhibitions-page">
      <SEOHead
        title="Exhibitions"
        description="Browse current, upcoming, and past exhibitions at The Coppini Academy of Fine Art in San Antonio, Texas."
        canonicalPath="/exhibitions"
      />

      {/* Hero Banner */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_92ecec5c-18a4-436e-a197-5f3e74d594af/artifacts/8vr8ffde_480938748_652291297458404_2764729676138038748_n%20Topaz%20Gigapixel%202x%20scale.jpg"
          alt="Exhibition opening at The Coppini Academy"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative h-full max-w-5xl mx-auto px-4 flex flex-col justify-center items-center text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-3">Exhibitions</h1>
          <p className="text-stone-200 text-base sm:text-lg max-w-2xl">
            Explore the exhibitions at The Coppini Academy of Fine Art, showcasing the talent of our members and guest artists.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {exhibitions.length === 0 ? (
          <div className="text-center py-16" data-testid="exhibitions-empty">
            <ImageIcon size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500 text-lg">No exhibitions at this time.</p>
            <p className="text-stone-400 text-sm mt-1">Check back soon for upcoming exhibitions.</p>
          </div>
        ) : (
          <>
            {/* Current / Now Showing */}
            {active.length > 0 && (
              <section className="mb-12" data-testid="exhibitions-active-section">
                <SectionHeading count={active.length}>Now Showing</SectionHeading>
                <div className="space-y-5">
                  {active.map(ex => (
                    <ExhibitionCard key={ex.id} exhibition={ex}
                      existingSubmission={userSubmissions.find(s => s.call_id === ex.open_call?.id)} />
                  ))}
                </div>
              </section>
            )}

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <section className="mb-12" data-testid="exhibitions-upcoming-section">
                <SectionHeading count={upcoming.length}>Upcoming</SectionHeading>
                <div className="space-y-5">
                  {upcoming.map(ex => (
                    <ExhibitionCard key={ex.id} exhibition={ex}
                      existingSubmission={userSubmissions.find(s => s.call_id === ex.open_call?.id)} />
                  ))}
                </div>
              </section>
            )}

            {/* Past — archival grid layout */}
            {past.length > 0 && (
              <section className="mb-12" data-testid="exhibitions-past-section">
                <SectionHeading count={past.length}>Past Exhibitions</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {past.map(ex => (
                    <Link
                      to={`/exhibitions/${ex.slug}`}
                      key={ex.id}
                      className="group block border border-stone-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
                      data-testid={`past-exhibition-card-${ex.slug}`}
                    >
                      {ex.hero_image_url ? (
                        <div className="aspect-[3/2] overflow-hidden">
                          <img src={ex.hero_image_url} alt={ex.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                        </div>
                      ) : (
                        <div className="aspect-[3/2] bg-stone-100 flex items-center justify-center">
                          <ImageIcon size={32} className="text-stone-300" />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-serif text-stone-800 group-hover:text-stone-900 mb-1">{ex.title}</h3>
                        <p className="text-xs text-stone-400">
                          {formatDateRange(ex.start_date, ex.end_date)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Exhibitions;
